import { useTrackers } from '@ps/web-shared-analytics';
import classNames from 'clsx';
import type { AnyObject } from 'final-form';
import type React from 'react';
import type { FieldMetaState } from 'react-final-form';
import { Field } from 'react-final-form';

import { TickIcon } from '../../../assets/icons';
import { withValidator } from '../../hoc';
import type { InjectedValidatorProps } from '../../hoc';
import { useDisplayLabel, usePauseValidator, useTrackingLabel } from '../../hooks';
import type { LabelProp } from '../../hooks';

import styles from './Toggle.module.scss';

export type RawProps = InjectedValidatorProps & {
  id: string;
  label?: React.ReactNode;
  error?: boolean;
  type: 'radio' | 'switch' | 'checkbox';
  name?: string;
  value?: string | boolean;
  checked?: boolean;
  className?: string;
  hideLabel?: boolean;
  onOffToggle?: boolean;
  align?: 'left' | 'right';
  variant?: 'primary' | 'secondary';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: FieldMetaState<any>;
} & React.InputHTMLAttributes<HTMLInputElement>;

export interface Props extends Omit<RawProps, 'label'> {
  label: LabelProp;
}

export const RawToggle: React.FC<RawProps> = ({
  label,
  hideLabel,
  meta,
  error,
  onOffToggle,
  id,
  checked,
  type,
  align = 'left',
  variant = 'primary',
  className,
  ...rest
}) => {
  const isSwitch = type === 'switch';
  const alignRight = align === 'right' && isSwitch;
  const variantClassName = styles[variant as keyof typeof styles] ?? null;

  return (
    <div
      className={classNames(styles.wrapper, variantClassName, {
        [styles.error]: (meta && meta.touched && (meta.error || meta.submitError)) || error,
        [styles.alignRight]: alignRight
      })}
    >
      <input
        {...rest}
        id={id}
        checked={checked}
        type={isSwitch ? 'checkbox' : type}
        className={classNames(styles.input, {
          [styles.toggleSwitch]: isSwitch,
          [styles.onOffToggle]: isSwitch && onOffToggle
        })}
      />
      <label
        htmlFor={id}
        className={classNames(
          styles.label,
          {
            [styles.switchLabel]: isSwitch,
            [styles.hidden]: hideLabel,
            [styles.emptyLabel]: !label
          },
          className
        )}
      >
        {isSwitch && <span className={styles.toggle} />}
        {type === 'checkbox' && <TickIcon />}
        {hideLabel || variant === 'secondary' ? <span>{label}</span> : label}
      </label>
    </div>
  );
};

export const ToggleAdapter = ({ input, meta, label, onChange, uiType, ...rest }: AnyObject) => {
  const { inputFilled } = useTrackers();
  const displayLabel = useDisplayLabel(label);
  const fieldName = useTrackingLabel(label);
  const checked = rest.checked !== undefined ? rest.checked : input.checked;

  return (
    <RawToggle
      {...rest}
      {...input}
      type={uiType}
      checked={checked}
      label={displayLabel}
      onChange={(event) => {
        inputFilled({ fieldName, fieldType: input.type, boolInput: event.target.checked });
        input.onChange?.(event);
        onChange?.(event);
      }}
      meta={meta}
    />
  );
};

const Toggle: React.FC<Props> = ({ validator, validationErrors, type, ...rest }) => {
  usePauseValidator();

  return (
    <Field
      {...rest}
      type={type === 'switch' ? 'checkbox' : type}
      uiType={type}
      name={rest.name ? rest.name : rest.id}
      component={ToggleAdapter}
      validate={validator}
    />
  );
};

export default withValidator(Toggle);
